import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';

const PLAN_LABEL  = { pro: 'Pro', enterprise: 'Enterprise', starter: 'Starter' };
const CYCLE_LABEL = { monthly: 'Monthly', annual: 'Annual' };
function fmt(num, sym) { return num == null ? '—' : `${sym || ''} ${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(num))}`.trim(); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'; }

// ── ProrationModal ─────────────────────────────────────────────────────────────
function ProrationModal({ preview, onConfirm, onCancel, submitting }) {
  const {
    currency, current_plan, subscription_expiry,
    target_plan, target_billing_cycle, new_plan_price,
    days_remaining, total_days_in_cycle, current_plan_price,
    unused_credit, due_now,
  } = preview;
  const hasCredit = unused_credit > 0;
  const dailyRate = total_days_in_cycle ? current_plan_price / total_days_in_cycle : 0;
  const renewal = preview.kind === 'renewal';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '1.25rem', padding: '2rem', width: '100%', maxWidth: 420 }}>
        <h2 style={{ color: '#f1f5f9', margin: '0 0 0.25rem', fontSize: '1.2rem', fontWeight: 700 }}>{renewal ? 'Payment summary' : 'Upgrade summary'}</h2>
        <p style={{ color: '#64748b', fontSize: '0.83rem', margin: '0 0 1.5rem' }}>
          {renewal
            ? <><strong style={{ color: '#a78bfa' }}>{PLAN_LABEL[target_plan]}</strong> · {CYCLE_LABEL[target_billing_cycle]} — adds one {target_billing_cycle === 'annual' ? 'year' : 'month'} to your subscription</>
            : <>{PLAN_LABEL[current_plan]} → <strong style={{ color: '#a78bfa' }}>{PLAN_LABEL[target_plan]}</strong> · {CYCLE_LABEL[target_billing_cycle]}</>}
        </p>

        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #1e293b' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.88rem' }}>{PLAN_LABEL[target_plan]} ({CYCLE_LABEL[target_billing_cycle]})</span>
            <span style={{ color: '#cbd5e1', fontSize: '0.88rem' }}>{fmt(new_plan_price, currency)}</span>
          </div>

          {hasCredit && (
            <div style={{ background: '#0d2b1d', border: '1px solid #166534', borderRadius: '0.6rem', padding: '0.75rem 1rem', margin: '0.5rem 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ color: '#86efac', fontSize: '0.85rem' }}>Unused {PLAN_LABEL[current_plan]} credit</span>
                <span style={{ color: '#4ade80', fontWeight: 700 }}>− {fmt(unused_credit, currency)}</span>
              </div>
              <div style={{ color: '#6ee7b7', fontSize: '0.75rem', marginTop: '0.3rem' }}>
                {days_remaining} days remaining
                {dailyRate > 0 && ` · ${fmt(dailyRate, currency)}/day`}
              </div>
              {subscription_expiry && (
                <div style={{ color: '#4ade80', fontSize: '0.73rem', marginTop: '0.15rem', opacity: 0.8 }}>
                  Current plan expires {fmtDate(subscription_expiry)}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 0', borderTop: '2px solid #334155', marginTop: hasCredit ? 0 : '0.5rem' }}>
            <span style={{ color: '#f1f5f9', fontWeight: 700 }}>Due on activation</span>
            <span style={{ color: '#a78bfa', fontWeight: 800, fontSize: '1.4rem' }}>{fmt(due_now, currency)}</span>
          </div>

          {hasCredit && (
            <div style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', marginTop: '-0.25rem' }}>
              From next billing: {fmt(new_plan_price, currency)}/{target_billing_cycle === 'annual' ? 'yr' : 'mo'}
            </div>
          )}
        </div>

        <p style={{ color: '#475569', fontSize: '0.78rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          No automatic charge. Pay using one of the methods on this page with your reference — we confirm the
          payment and activate your plan within 24 hours, and email you a receipt.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{ flex: 1, padding: '0.8rem', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Back
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{ flex: 2, padding: '0.8rem', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.95rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? 'Submitting…' : `Confirm — ${fmt(due_now, currency)} due`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Plan data ──────────────────────────────────────────────────────────────────
const TIERS = {
  pro: {
    name: 'Pro',
    accent: '#6366f1',
    glow: 'rgba(99,102,241,0.4)',
    edge: '#4338ca',
    gradient: 'linear-gradient(160deg,#312e81 0%,#1e1b4b 100%)',
    features: [
      { icon: '👥', text: 'Up to 5 team members' },
      { icon: '📊', text: 'Financial reports' },
      { icon: '📶', text: 'Multi-device sync' },
      { icon: '📤', text: 'Data export (CSV)' },
      { icon: '🔑', text: 'Custom permissions' },
      { icon: '🛒', text: 'Purchase tracking' },
      { icon: '🫙', text: 'Production runs' },
      { icon: '🎯', text: 'Priority support' },
    ],
  },
  enterprise: {
    name: 'Enterprise',
    accent: '#7c3aed',
    glow: 'rgba(124,58,237,0.4)',
    edge: '#5b21b6',
    gradient: 'linear-gradient(160deg,#3b0764 0%,#1e1b4b 100%)',
    features: [
      { icon: '✦', text: 'Everything in Pro' },
      { icon: '🏢', text: 'Multiple businesses' },
      { icon: '📈', text: 'Parent company dashboard' },
      { icon: '🔢', text: 'Consolidated P&L & ratios' },
      { icon: '💰', text: 'Fund & capital tracking' },
      { icon: '⚠️', text: 'Underperformer detection' },
    ],
  },
};

const DEFAULT_PRICING = {
  pro:        { monthly: 9.99,  annual: 109.89 },
  enterprise: { monthly: 14.99, annual: 164.89 },
};

// ── 3D plan card ───────────────────────────────────────────────────────────────
function PlanCard({ t, isSelected, pricing, currency, cycle, saving, onClick, previewing, isPending, currentPlan }) {
  const [hovered, setHovered] = useState(false);
  const price = cycle === 'monthly' ? pricing.monthly : pricing.annual;
  const priceLabel = cycle === 'monthly' ? `${currency} ${pricing.monthly}/mo` : `${currency} ${pricing.annual}/yr`;
  const active = isSelected || hovered;

  const unavailable = currentPlan === t;

  return (
    <button
      onClick={onClick}
      disabled={previewing || unavailable}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: active ? t.gradient : '#131c2e',
        border: `2px solid ${active ? t.accent : '#1e293b'}`,
        borderRadius: '1.1rem',
        padding: '1.4rem 1.1rem 1.6rem',
        cursor: (previewing || unavailable) ? 'default' : 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        transform: active ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: active
          ? `0 1px 0 rgba(255,255,255,0.1) inset, 0 8px 0 ${t.edge}, 0 18px 40px ${t.glow}`
          : `0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 0 #0a0e1a, 0 10px 24px rgba(0,0,0,0.45)`,
        opacity: unavailable ? 0.45 : 1,
      }}
    >
      {unavailable && (
        <div style={{ position: 'absolute', top: '0.6rem', right: '0.6rem', background: '#1e293b', color: '#64748b', fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: '4px', textTransform: 'uppercase' }}>
          Current
        </div>
      )}
      {cycle === 'annual' && !unavailable && (
        <div style={{ position: 'absolute', top: '0.6rem', right: '0.6rem', background: '#166534', color: '#86efac', fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
          SAVE {saving}%
        </div>
      )}

      <div style={{ fontWeight: 800, fontSize: '1.05rem', color: active ? '#f1f5f9' : '#94a3b8', marginBottom: '0.3rem' }}>{t.name}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: active ? '#fff' : '#cbd5e1', lineHeight: 1.1 }}>
        {currency} {price}
      </div>
      <div style={{ fontSize: '0.72rem', color: active ? 'rgba(255,255,255,0.55)' : '#475569', marginTop: '0.15rem', marginBottom: '1rem' }}>
        {cycle === 'monthly' ? 'per month' : 'per year'}
      </div>

      <div style={{ borderTop: `1px solid ${active ? 'rgba(255,255,255,0.1)' : '#1e293b'}`, paddingTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {t.features.map(({ icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: active ? 'rgba(255,255,255,0.85)' : '#64748b' }}>
            <span style={{ fontSize: '0.85rem', flexShrink: 0 }}>{icon}</span>
            {text}
          </div>
        ))}
      </div>

      {!unavailable && (
        <div style={{
          marginTop: '1.1rem',
          padding: '0.6rem',
          borderRadius: '0.6rem',
          background: active ? 'rgba(255,255,255,0.12)' : '#1e293b',
          color: active ? '#fff' : '#64748b',
          fontSize: '0.82rem',
          fontWeight: 700,
          textAlign: 'center',
          transition: 'all 0.18s',
        }}>
          {previewing ? 'Calculating…' : `Select ${t.name} →`}
        </div>
      )}
    </button>
  );
}

// ── Subscription status + how to pay ──────────────────────────────────────────
const STATE_TEXT = {
  complimentary: (b) => b.complimentary_until ? `Complimentary until ${fmtDate(b.complimentary_until)} — nothing to pay` : 'Complimentary — nothing to pay',
  active:    (b) => b.subscription_expiry ? `Paid until ${fmtDate(b.subscription_expiry)}` : 'Active',
  trial:     (b) => `Free trial — ends ${fmtDate(b.trial_ends_at)}`,
  grace:     (b) => `Payment due — read-only from ${fmtDate(b.grace_ends_at)}`,
  read_only: ()  => 'Lapsed — read-only until paid (your records are safe)',
};

function BillingStatus({ billing, pay, onRenew, busy, pending, cycle, setCycle, currency, pricing }) {
  if (!billing) return null;
  const tone = billing.state === 'read_only' ? '#ef4444' : billing.state === 'grace' ? '#f59e0b' : '#22c55e';
  const comp = billing.state === 'complimentary';
  const price = pricing?.[billing.plan]?.[cycle];
  return (
    <div style={{ background: '#131c2e', border: `1px solid ${tone}`, borderRadius: '1rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
      <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Your subscription</div>
      <div style={{ color: '#f1f5f9', fontSize: '1.15rem', fontWeight: 700, marginTop: '0.3rem' }}>{PLAN_LABEL[billing.plan] || 'Starter'} plan</div>
      <div style={{ color: tone, fontSize: '0.88rem', marginTop: '0.15rem' }}>{(STATE_TEXT[billing.state] || STATE_TEXT.active)(billing)}</div>

      {!comp && <>
      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #1e293b' }}>
        <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.4rem' }}>How to pay</div>
        {pay?.methods?.length ? (
          <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.1rem', color: '#cbd5e1', fontSize: '0.88rem', lineHeight: 1.7 }}>
            {pay.methods.map(m => <li key={m.method}><strong>{m.label}:</strong> {m.detail}</li>)}
          </ul>
        ) : (
          <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 0.5rem' }}>
            Orange Money, MyZaka, Smega or EFT — request a payment below and we'll send you the details.
          </p>
        )}
        <div style={{ color: '#cbd5e1', fontSize: '0.88rem' }}>
          Use this reference: <strong style={{ color: '#a78bfa', letterSpacing: '0.05em' }}>{pay?.reference}</strong>
        </div>
        {pay?.contact && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.25rem' }}>Questions: {pay.contact}</div>}
      </div>

      {pending ? (
        <div style={{ marginTop: '1rem', color: '#fde68a', fontSize: '0.85rem' }}>
          ⏳ Your {pending.kind === 'renewal' ? 'payment' : 'upgrade'} request ({PLAN_LABEL[pending.requested_plan]}, {pending.billing_cycle}) is waiting for us to confirm your payment.
        </div>
      ) : (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={cycle} onChange={e => setCycle(e.target.value)} style={{ width: 'auto' }}>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
          <button onClick={onRenew} disabled={busy} className="btn-primary" style={{ padding: '0.55rem 1rem' }}>
            {busy ? 'Loading…' : `${billing.state === 'active' ? 'Renew' : 'Pay for'} ${PLAN_LABEL[billing.plan] || 'Starter'}${price != null ? ` — ${fmt(price, currency)}` : ''}`}
          </button>
        </div>
      )}
      </>}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function UpgradePlan() {
  const { parentCompany, reload } = useAuth();
  const navigate = useNavigate();

  const currentPlan    = parentCompany?.plan || 'starter';
  const availableTiers = currentPlan === 'enterprise' ? [] : currentPlan === 'pro' ? ['enterprise'] : ['pro', 'enterprise'];
  const [billing, setBilling] = useState(null);

  const [cycle,      setCycle]      = useState('monthly');
  const [pricing,    setPricing]    = useState(DEFAULT_PRICING);
  const [currency,   setCurrency]   = useState('USD');
  const [status,     setStatus]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);
  const [error,      setError]      = useState('');

  const loadBilling = () => settingsAPI.billing().then(({ data }) => {
    setBilling(data);
    setPricing({ ...DEFAULT_PRICING, ...data.pricing });
    setCurrency(data.currency);
    setStatus(data.pending_request ? { status: 'pending', ...data.pending_request } : null);
  });

  useEffect(() => {
    loadBilling().catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleCardClick = async (tier) => {
    setError('');
    setSelectedTier(tier);
    setPreviewing(true);
    try {
      const r = await settingsAPI.previewUpgrade(tier, cycle);
      setPreview(r.data.preview);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load upgrade summary. Please try again.');
      setSelectedTier(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await settingsAPI.requestUpgrade({ plan: selectedTier, billing_cycle: cycle });
      setPreview(null);
      setDone(true);
      loadBilling().catch(() => {});
      reload();
    } catch (err) {
      setPreview(null);
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem', color: '#94a3b8', textAlign: 'center' }}>Loading…</div>;

  const saving = (t) => {
    const p = pricing[t] || DEFAULT_PRICING[t];
    return Math.round((p.monthly * 12 - p.annual) / (p.monthly * 12) * 100);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem 1rem' }}>
      {preview && (
        <ProrationModal
          preview={preview}
          onConfirm={handleConfirm}
          onCancel={() => { setPreview(null); setSelectedTier(null); }}
          submitting={submitting}
        />
      )}

      <div style={{ maxWidth: 640, width: '100%' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '1.5rem', padding: 0 }}>
          ← Back
        </button>

        <h1 style={{ color: '#f1f5f9', fontSize: '1.6rem', fontWeight: 800, marginBottom: '0.35rem' }}>
          Plan &amp; billing
        </h1>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
          No card is charged — you pay by mobile money or EFT and we confirm it.
        </p>

        {!done && (
          <BillingStatus
            billing={billing?.billing} pay={billing?.pay} pending={billing?.pending_request}
            cycle={cycle} setCycle={setCycle} currency={currency} pricing={billing?.pricing}
            busy={previewing} onRenew={() => handleCardClick(billing?.billing?.plan || currentPlan)}
          />
        )}
        {error && !availableTiers.length && <p style={{ color: '#f87171', fontSize: '0.88rem', textAlign: 'center' }}>{error}</p>}

        {done ? (
          <div style={{ background: '#1e293b', borderRadius: '1rem', padding: '2rem', textAlign: 'center', border: '1px solid #22c55e' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: '#f1f5f9', marginBottom: '0.5rem' }}>Request submitted!</h2>
            <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>
              Pay using the details above with reference <strong style={{ color: '#f1f5f9' }}>{billing?.pay?.reference}</strong>.
              Once we see it we'll activate your {PLAN_LABEL[selectedTier]} plan within <strong style={{ color: '#f1f5f9' }}>24 hours</strong> and email you a receipt.
            </p>
          </div>
        ) : status?.status === 'pending' || availableTiers.length === 0 ? null : (
          <>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.1rem', margin: '0 0 0.75rem' }}>
              {currentPlan === 'pro' ? 'Upgrade to Enterprise' : 'Upgrade your plan'}
            </h2>
            {/* Billing cycle toggle */}
            <div style={{ display: 'flex', background: '#131c2e', borderRadius: '0.75rem', padding: '0.3rem', marginBottom: '1.5rem', border: '1px solid #1e293b' }}>
              {[
                { key: 'monthly', label: 'Monthly' },
                { key: 'annual',  label: 'Annual · save ~8%' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setCycle(opt.key)}
                  style={{
                    flex: 1, padding: '0.55rem', border: 'none', borderRadius: '0.55rem', cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.15s',
                    background: cycle === opt.key
                      ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                      : 'transparent',
                    color: cycle === opt.key ? '#fff' : '#64748b',
                    boxShadow: cycle === opt.key
                      ? '0 2px 8px rgba(99,102,241,0.35)'
                      : 'none',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Plan cards side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: availableTiers.length > 1 ? '1fr 1fr' : '1fr', gap: '1rem', marginBottom: '1rem' }}>
              {availableTiers.map(t => (
                <PlanCard
                  key={t}
                  t={TIERS[t]}
                  isSelected={selectedTier === t && previewing}
                  pricing={pricing[t] || DEFAULT_PRICING[t]}
                  currency={currency}
                  cycle={cycle}
                  saving={saving(t)}
                  onClick={() => handleCardClick(t)}
                  previewing={previewing}
                  currentPlan={currentPlan}
                />
              ))}
            </div>

            {error && <p style={{ color: '#f87171', marginTop: '0.75rem', fontSize: '0.88rem', textAlign: 'center' }}>{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
